import { PublicClientApplication, type AccountInfo } from '@azure/msal-browser';

// Microsoft Entra External ID (CIAM) sign-in — the app's only auth (EasyAuth retired).
// Config verified against the tenant discovery doc (see build/multi-tenant-auth.md).
//
// REDIRECT flow (not popup): loginRedirect navigates the whole tab to Entra and back,
// and initMsal() calls handleRedirectPromise() on boot to finish the exchange. This
// avoids the nested-popup class of bug entirely (Safari blocks popups aggressively).
//
// The backend validates the ID token's `aud` against this client id, and — crucially —
// the token is sent to the API in the `X-Auth-Token` header, NOT Authorization (SWA
// strips Authorization on managed functions). See core/api.ts.

const CLIENT_ID = 'fda30bb4-0b01-432d-a275-f50f12c292f0';
const AUTHORITY = 'https://tpiap.ciamlogin.com/29a29d0e-e1bd-44c0-9558-963e1df07f06';

export const SCOPES = ['openid', 'profile', 'email', 'offline_access'];

export const msal = new PublicClientApplication({
  auth: {
    clientId: CLIENT_ID,
    authority: AUTHORITY,
    knownAuthorities: ['tpiap.ciamlogin.com'],
    // The app origin — a registered SPA redirect URI (ashy-wave URL,
    // www.thisplaceisaprison.co.uk, http://localhost:4280/:5173).
    redirectUri: window.location.origin,
  },
  cache: { cacheLocation: 'localStorage' },
});

let ready: Promise<void> | null = null;
// The ID token MSAL hands back on the sign-in round-trip. acquireTokenSilent can
// throw right after login (no persisted account yet, or a silent-refresh hiccup),
// which would otherwise leave the very first /api/me tokenless -> 401 -> back to the
// Landing (the sign-in loop). Keeping this token lets getIdToken() serve a valid
// token immediately after sign-in even if the silent path is momentarily unhappy.
let lastIdToken: string | null = null;

// Loop-proofing. When the tab leaves for Entra we set an attempt flag; a successful
// return clears it. If we boot with the flag STILL set but no session, we know the
// sign-in round-trip came back empty — so the UI can show a clear "didn't complete,
// retry" instead of silently dropping to the Landing (which reads as an infinite
// loop). lastAuthError carries the reason so that message is specific, not generic.
const ATTEMPT_KEY = 'tpiap:auth-attempt';
let lastAuthError: string | null = null;
const markAttempt = () => { try { sessionStorage.setItem(ATTEMPT_KEY, '1'); } catch { /* private mode */ } };
const clearAttempt = () => { try { sessionStorage.removeItem(ATTEMPT_KEY); } catch { /* ignore */ } };
const authErr = (e: unknown): string => {
  const x = e as { errorCode?: string; errorMessage?: string; message?: string } | null;
  return String((x && (x.errorCode || x.errorMessage || x.message)) || 'unknown_error').slice(0, 200);
};

// First-party session. After the ONE MSAL login we exchange the Entra token for a
// 30-day httpOnly cookie (POST /api/auth/session). This flag marks that we have one,
// so the request layer can authenticate via the cookie and SKIP acquireTokenSilent
// entirely — the acquireTokenSilent call to ciamlogin.com is exactly what privacy/
// wallet extensions block, and skipping it is what keeps the app signed in.
const SESSION_FLAG = 'tpiap:session';
export function hasServerSession(): boolean {
  try { return localStorage.getItem(SESSION_FLAG) === '1'; } catch { return false; }
}
// Mark that we have a confirmed first-party session, so the request layer SKIPS the
// (extension-blockable) MSAL path. Called both after establishSession AND whenever an
// authenticated request succeeds — the latter is how MAGIC-LINK users (no MSAL account,
// cookie set by the verify redirect) get onto the cookie-only path. Backend contract:
// build/handoff-auth-and-deploy.md.
export function markServerSession(): void {
  try { localStorage.setItem(SESSION_FLAG, '1'); } catch { /* ignore */ }
}
export function clearServerSession(): void {
  try { localStorage.removeItem(SESSION_FLAG); } catch { /* ignore */ }
}
async function establishSession(idToken: string): Promise<void> {
  try {
    const r = await fetch('/api/auth/session', {
      method: 'POST',
      headers: { 'X-Auth-Token': idToken },
      credentials: 'same-origin',
    });
    if (r.ok) markServerSession();
  } catch { /* best-effort; the token still works as X-Auth-Token this session */ }
}

/**
 * Re-mint the session cookie from an MSAL token we already hold — used by the request
 * layer when a call 401s (the cookie may have expired, or been transiently rejected
 * during an API cold-start). Prefers the redirect token / silent cache; NEVER opens a
 * window. Returns true only if a fresh cookie was installed. If there's no token at
 * all, the user is genuinely signed out → returns false (caller drops to sign-in).
 */
export async function refreshSession(): Promise<boolean> {
  const token = await getIdToken();
  if (!token) return false;
  await establishSession(token);
  return hasServerSession();
}

// Diagnostic beacon: report an auth failure to the backend (fire-and-forget,
// keepalive so it survives the redirect navigation). Lets the sign-in loop be
// root-caused from server logs instead of the user's console. Failure-path only.
function beacon(kind: string, detail: Record<string, unknown>): void {
  try {
    fetch('/api/client-log', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ kind, detail: { ...detail, url: location.href.slice(0, 200) } }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* ignore */ }
}

/**
 * Post-boot auth state for the UI. `attempted && !hasAccount` means a sign-in
 * round-trip came back without a session — the loop condition. `error` is the reason
 * (from handleRedirectPromise / acquireTokenSilent) when we have one.
 */
export function authTrouble(): { attempted: boolean; hasAccount: boolean; error: string | null } {
  let attempted = false;
  try { attempted = sessionStorage.getItem(ATTEMPT_KEY) != null; } catch { /* ignore */ }
  return { attempted, hasAccount: !!currentAccount(), error: lastAuthError };
}

/**
 * MSAL v5 requires initialize() before any other call. We also run
 * handleRedirectPromise() here so that when the tab returns from Entra with an auth
 * response in the URL, the token exchange completes and the account is set — before
 * anything checks currentAccount() or asks for a token.
 */
export function initMsal(): Promise<void> {
  if (!ready) {
    // Did we land here FROM Entra (an auth response in the URL)? Used to tell a
    // genuine post-redirect failure apart from a normal signed-out page load.
    const hadCode = /[?#&](code|error|state)=/.test(window.location.href);
    ready = msal
      .initialize()
      .then(() => msal.handleRedirectPromise())
      .then(async (res) => {
        if (res?.account) { msal.setActiveAccount(res.account); clearAttempt(); } // sign-in landed
        if (res?.idToken) {
          lastIdToken = res.idToken; // fresh token from this sign-in
          await establishSession(res.idToken); // → 30-day cookie; from here MSAL is not needed
        }
        // Came back from Entra but no account resulted — THE silent-loop condition.
        if (hadCode && !res?.account) beacon('redirect_no_account', { hadCode, hasRes: !!res });
      })
      .catch((e) => {
        // A failed/absent redirect response is normally fine (app shows the Landing),
        // but record + log + beacon it so a real exchange failure isn't invisible.
        lastAuthError = authErr(e);
        beacon('redirect_error', { error: lastAuthError, hadCode });
        console.error('[auth] handleRedirectPromise failed:', e);
      });
  }
  return ready;
}

export function currentAccount(): AccountInfo | null {
  return msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
}

/**
 * Interactive sign-in / sign-up. Navigates the tab to Entra (does not return).
 * `force` re-authenticates from scratch (prompt=login) AND drops any stale local
 * MSAL cache first — the escape hatch when a cached account's token won't refresh
 * and the app is stuck bouncing to the Landing.
 */
export async function signIn(force = false): Promise<void> {
  await initMsal();
  lastAuthError = null;
  if (force) {
    // Clear the stuck account/token cache so Entra issues a completely fresh session.
    try { await msal.clearCache(); } catch { /* older msal / already clear */ }
    lastIdToken = null;
  }
  markAttempt(); // cleared on a successful return; else the UI shows "didn't complete"
  await msal.loginRedirect(force ? { scopes: SCOPES, prompt: 'login' } : { scopes: SCOPES });
}

/**
 * A fresh ID token for the signed-in account, refreshed silently (offline_access is on,
 * so the session survives far past the old ~8h limit). Null when nobody is signed in OR
 * when a silent refresh needs a user present — the caller then gets a 401 and the app
 * shows the Landing, whose button does the interactive (redirect) sign-in. No interactive
 * prompt here: this runs on EVERY request and must never open a window.
 */
export async function getIdToken(): Promise<string | null> {
  await initMsal();
  const account = currentAccount();
  if (account) {
    try {
      const res = await msal.acquireTokenSilent({ scopes: SCOPES, account });
      lastIdToken = res.idToken; // keep the freshest token for the fallback below
      clearAttempt(); // we have a live session
      return res.idToken;
    } catch (e) {
      // Don't swallow: a silent-refresh failure right after sign-in is exactly what
      // was causing the tokenless first request. Fall through to lastIdToken.
      lastAuthError = authErr(e);
      beacon('silent_error', { error: lastAuthError, hadLast: !!lastIdToken });
      console.error('[auth] acquireTokenSilent failed, using redirect token:', e);
    }
  }
  // Fallback to the token MSAL issued on the sign-in round-trip. Covers "account not
  // persisted yet" and "silent acquisition threw" — both otherwise 401 the app back
  // to the Landing.
  if (lastIdToken) { clearAttempt(); return lastIdToken; }
  // No account AND no redirect token: signed out, or a sign-in that didn't stick.
  // Leave the attempt flag alone — authTrouble() uses it to show the retry screen.
  let attempted = false;
  try { attempted = sessionStorage.getItem(ATTEMPT_KEY) != null; } catch { /* ignore */ }
  // Beacon ONLY the problem case — a cached account we can't get a token for, or a
  // sign-in that was attempted but didn't stick. A plain never-signed-in visitor
  // (no account, no attempt) is normal and stays silent.
  if (account || attempted) beacon('no_token', { hasAccount: !!account, attempted, error: lastAuthError });
  console.warn('[auth] no token (account:', !!account, 'redirect-token: no) → request goes out unauthenticated');
  return null;
}

export async function signOut(): Promise<void> {
  await initMsal();
  clearServerSession();
  try { await fetch('/api/auth/session', { method: 'DELETE', credentials: 'same-origin' }); } catch { /* ignore */ }
  await msal.logoutRedirect({ account: currentAccount() ?? undefined });
}
